import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { QrCode } from "lucide-react";
import { api, fmtErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const inputCls =
  "w-full h-12 px-4 rounded-xl border border-zinc-200 bg-white text-base placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-950 focus:ring-offset-2 transition-shadow";

export default function Activate() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [loadingInfo, setLoadingInfo] = useState(true);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptDpa, setAcceptDpa] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) {
      setError("El enlace no es válido o ha caducado.");
      setLoadingInfo(false);
      return;
    }
    api
      .get(`/auth/invitations/${token}`)
      .then(({ data }) => setInfo(data))
      .catch((e) => setError(fmtErr(e, "El enlace no es válido o ha caducado.")))
      .finally(() => setLoadingInfo(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Las contraseñas no coinciden.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/invitations/activate", {
        token,
        password,
        accept_terms: acceptTerms,
        accept_dpa: acceptDpa,
      });
      setUser(data);
      navigate("/app", { replace: true });
    } catch (err) {
      setError(fmtErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex flex-col items-center justify-center px-4 py-12" data-testid="activate-page">
      <div className="w-full max-w-md animate-fade-up">
        <div className="flex flex-col items-center mb-8">
          <span className="h-14 w-14 rounded-2xl bg-zinc-900 text-white flex items-center justify-center mb-4">
            <QrCode size={26} />
          </span>
          <h1 className="font-heading text-3xl font-bold tracking-tighter">Asset Passport</h1>
          <p className="text-zinc-500 text-sm mt-1.5 text-center">Activación de cuenta de administrador.</p>
        </div>

        <div className="bg-white rounded-3xl border border-zinc-200/80 card-shadow p-7 sm:p-8">
          {loadingInfo ? (
            <div className="flex justify-center py-8">
              <div className="h-8 w-8 rounded-full border-2 border-zinc-300 border-t-zinc-900 animate-spin" data-testid="activate-loading" />
            </div>
          ) : info ? (
            <form onSubmit={submit} className="space-y-4" data-testid="activate-form">
              <div className="bg-zinc-50 border border-zinc-200 rounded-xl px-4 py-3.5 space-y-1" data-testid="activate-info">
                <p className="text-sm text-zinc-900 font-medium">{info.company_name}</p>
                <p className="text-sm text-zinc-600">{info.name}</p>
                <p className="text-sm text-zinc-500">{info.email}</p>
              </div>
              <p className="text-sm text-zinc-600 leading-relaxed">
                Confirma tus datos y elige la contraseña de tu cuenta de administrador.
              </p>
              <input
                className={inputCls}
                type="password"
                placeholder="Nueva contraseña"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                autoComplete="new-password"
                data-testid="activate-password-input"
              />
              <input
                className={inputCls}
                type="password"
                placeholder="Repite la contraseña"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                minLength={6}
                autoComplete="new-password"
                data-testid="activate-password-confirm-input"
              />
              {error && (
                <p className="text-sm text-rose-600 bg-rose-50 border border-rose-100 rounded-xl px-4 py-2.5" data-testid="activate-error">
                  {error}
                </p>
              )}
              <div className="space-y-3.5 border-t border-zinc-100 pt-4">
                <label className="flex items-start gap-3 text-left cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-0.5 h-5 w-5 shrink-0 rounded border-zinc-300 accent-zinc-900"
                    checked={acceptTerms}
                    onChange={(e) => setAcceptTerms(e.target.checked)}
                    data-testid="activate-terms-checkbox"
                  />
                  <span className="text-xs text-zinc-600 leading-relaxed">
                    He leído y acepto los{" "}
                    <Link to="/terms" target="_blank" className="underline underline-offset-2 hover:text-zinc-900">
                      Términos y condiciones de uso
                    </Link>{" "}
                    y confirmo que actúo en nombre de una empresa o en calidad profesional. He leído la{" "}
                    <Link to="/privacy" target="_blank" className="underline underline-offset-2 hover:text-zinc-900">
                      Política de privacidad
                    </Link>
                    .
                  </span>
                </label>
                <label className="flex items-start gap-3 text-left cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-0.5 h-5 w-5 shrink-0 rounded border-zinc-300 accent-zinc-900"
                    checked={acceptDpa}
                    onChange={(e) => setAcceptDpa(e.target.checked)}
                    data-testid="activate-dpa-checkbox"
                  />
                  <span className="text-xs text-zinc-600 leading-relaxed">
                    En nombre de mi organización, acepto el{" "}
                    <Link to="/dpa" target="_blank" className="underline underline-offset-2 hover:text-zinc-900">
                      Acuerdo de Encargo del Tratamiento (DPA)
                    </Link>{" "}
                    aplicable cuando Asset Passport trate datos personales por cuenta de la organización.
                  </span>
                </label>
              </div>
              <button
                type="submit"
                disabled={loading || !acceptTerms || !acceptDpa}
                data-testid="activate-submit-button"
                className="w-full h-12 rounded-full bg-zinc-900 text-white font-medium hover:bg-zinc-800 active:scale-[0.98] transition-[background-color,transform] duration-200 disabled:opacity-50 focus-visible:ring-2 focus-visible:ring-zinc-950 focus-visible:ring-offset-2"
              >
                {loading ? "Activando…" : "Activar mi cuenta"}
              </button>
            </form>
          ) : (
            <div className="text-center" data-testid="activate-invalid">
              <p className="text-sm text-rose-600 bg-rose-50 border border-rose-100 rounded-xl px-4 py-3" data-testid="activate-error">
                {error}
              </p>
              <Link
                to="/login"
                className="inline-block mt-5 text-sm font-medium text-zinc-500 hover:text-zinc-900 underline underline-offset-4 transition-colors"
                data-testid="activate-back-login"
              >
                Volver a iniciar sesión
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
